import {inject, Injectable, signal} from '@angular/core';
import {CompanyService, ErrorService, StateManagerService} from '@core/services';
import {CompanyState, Membership, StateManager} from '@core/types';
import {CONFIG, log, SLEEP_FOR, STORAGE_KEYS} from '@core/utils';
import {AuthStateService} from '@auth/states';
import {Router} from '@angular/router';
import {JwtMembership} from '@auth/types';

@Injectable({
  providedIn: 'root'
})
export class CompanyStateService {

  private stateManager = inject(StateManagerService);
  private companyService = inject(CompanyService);
  private authState = inject(AuthStateService);
  private errorService = inject(ErrorService);
  private router = inject(Router);

  private state: StateManager<CompanyState> = this.stateManager.createState<CompanyState>({
    storage: 'localStorage',
    key: STORAGE_KEYS.COMPANY,
    storageOptions: {
      ttl: CONFIG.TTL_APP,
      prefix: 'app',
      encrypt: false
    },
    initialValue: {
      memberships: [],
      currentMembership: null,
    },
  });

  memberships = this.state.computed(state => state.memberships);
  membership = this.state.computed(state => state.currentMembership);
  isCompanySelected = this.state.computed(state => !!state.currentMembership);
  logoRevision = signal(Date.now());

  async updateMemberships(force = false) {
    const r = await this.companyService.memberships({skipLoader: true, force});

    this.state.update(current => {
      const memberships = r.memberships || [];
      const currentMembership = memberships.find(m => m.id === current.currentMembership?.id);
      if (current.currentMembership && !currentMembership) {
        log.debug('Stored membership no longer valid. Clearing selection.');
      }
      if (!currentMembership && memberships.length === 1) {
        log.debug('Auto-selecting single membership:', memberships[0]?.id);
      }
      if (!this.authState.isAdmin() && memberships.length === 0) {
        this.errorService.setError({
          message: 'No tienes empresas asociadas. Contacta a soporte para asignar una empresa.'
        }, 403);
        this.router.navigate(['/error'], {queryParams: {reason: 'no-memberships'}});
      }
      return ({
      ...current,
      memberships: memberships,
      currentMembership: currentMembership || (memberships.length === 1 ? memberships[0] : null) ,
    })});
  }

  async selectMembership(membership: Membership) {
    this.state.update(current => {
      return ({
        ...current,
      currentMembership: membership ,
    })});
  }

  hydrateFromJwtMemberships(jwtMemberships?: JwtMembership[]) {
    if (!Array.isArray(jwtMemberships) || !jwtMemberships.length) {
      return;
    }

    const mapped: Membership[] = jwtMemberships.map((item, index) => {
      const companyId = item.companyId || `jwt-company-${index + 1}`;
      const roleSlug = String(item.role || 'USER').toUpperCase();
      const roleName = roleSlug === 'OWNER'
        ? 'Dueño de la cuenta'
        : roleSlug === 'EMPLOYEE'
        ? 'Empleado'
        : roleSlug;
      const roleId = roleSlug === 'OWNER' ? 1 : roleSlug === 'EMPLOYEE' ? 2 : 0;
      const role = {id: roleId, name: roleName, slug: roleSlug};

      return {
        id: `jwt-membership-${companyId}-${roleSlug}`,
        company: {
          id: companyId,
          name: item.companyName || `Empresa ${companyId}`,
          role,
          summary: {employees: 0, policies: 0, courses: 0},
          capital: 0,
          countries: []
        },
        role
      };
    });

    this.state.update(current => ({
      ...current,
      memberships: mapped,
      currentMembership: mapped.length === 1 ? mapped[0] : current.currentMembership
    }));
  }

  bumpLogoRevision() {
    this.logoRevision.set(Date.now());
  }

  isEmployeeMembership(membership?: Membership | null): boolean {
    const role = membership?.role;
    if (!role) {
      return false;
    }

    return role.slug === 'EMPLOYEE' || role.name === 'Empleado';
  }

  isOwnerMembership(membership?: Membership | null): boolean {
    const role = membership?.role;
    if (!role) {
      return false;
    }

    return role.slug === 'OWNER' || role.name === 'Dueño de la cuenta';
  }

  async initialize() {
    await this.state.loadFromStorage();
    if (!this.memberships().length) {
      await this.updateMemberships();
      await SLEEP_FOR(0.2);
    }
    log.debug('Company state initialized');
  }
}
